/* eslint-disable react/jsx-props-no-spreading */
import { faPenToSquare } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Checkbox, Stack, Tooltip } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-premium';
import { get, isEmpty } from 'lodash-es';
import queryString from 'query-string';
import React from 'react';
import { generatePath, Link as RouterLink, useNavigate } from 'react-router-dom';

import { API_ADGROUP_CREATIVE_ATTACH_URL, API_ADGROUP_CREATIVE_DETACH_URL } from '../../constants/apiUrls';
import { ADVERTISERS_CREATIVES_EDIT_URL, PREVIEW_STUDIO_URL } from '../../constants/routeUrls';
import { useConfig } from '../../hooks/useConfig';
import { useConstants } from '../../hooks/useConstants';
import { useSnackBar } from '../../hooks/useSnackBar';
import { formatServerDate, mmddyy } from '../../util/helpers';
import { snakeCaseKeys } from '../../util/object';
import { deleteRequestJSON, postRequestJSON } from '../../util/services';
import { isAggregateCell } from '../../util/tableUtils';
import DataGrid, { DataGridProps } from '../shared/DataGrid';
import { AssociationType } from './CreativesForm';
import QRCode from './PreviewQRCode';

const COLUMN_WIDTHS_KEY = 'creativesDataGridColumnWidths';

interface CreativesDataGridProps extends Omit<DataGridProps, 'columns'> {
  isLoading: boolean;
  creativesList: any;
  contractId?: number;
  lineItemId?: number;
  adGroupId?: number;
  productId?: number;
  associationType?: AssociationType;
  refetch: Function;
}

function CreativesDataGrid({
  isLoading,
  contractId,
  lineItemId,
  adGroupId,
  creativesList,
  productId,
  associationType,
  refetch,
  ...rest
}: CreativesDataGridProps) {
  const { apiHost } = useConfig();
  const { constants } = useConstants();
  const { showSnackBar } = useSnackBar();
  const navigate = useNavigate();

  const ALL_AD_TYPES_VALUE_TO_NAME = get(constants, 'CREATIVE.ALL_AD_TYPES_VALUE_TO_NAME', {});

  const getAdTypeLabel = (adType) => {
    let returnValue;
    const matchingAdtype = Object.entries(ALL_AD_TYPES_VALUE_TO_NAME).find(
      ([value]) => Number(value) === Number(adType)
    );
    if (matchingAdtype) {
      [, returnValue] = matchingAdtype;
    }
    return returnValue;
  };

  const extractFilename = (path) => path.substr(path.lastIndexOf('/') + 1);

  const getPreview = (creative) => {
    let jsx;
    if (creative.videoAd?.rawVideoS3Path) {
      const link = `${apiHost}/admin/helpers/s3_get_resource/${encodeURIComponent(creative.videoAd.rawVideoS3Path)}`;
      jsx = (
        <a href={link} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
          {extractFilename(creative.videoAd.rawVideoS3Path)}
        </a>
      );
    } else {
      const link = creative.link || creative.momentNativeAd?.url;
      if (link) {
        const redirectUrl = `https://production-creative-preview.360platform.inmarket.com/?p=d&auto_tag=${encodeURIComponent(
          creative.tag
        )}`;
        jsx = (
          <a href={redirectUrl} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
            <img alt={creative.name} src={link} style={{ maxHeight: '50px' }} />
          </a>
        );
      }
    }

    return jsx;
  };

  const getTargetedCreativeId = (creative) =>
    creative.association?.associated ? creative.association.targetedCreativeId : '';

  const navigateToCreativeForm = (creativeId, newTab) => {
    // We need to re-find the creative by ID because the values may change
    const creative = creativesList.collection?.find((item) => item.id === creativeId);
    const editLink = `${generatePath(ADVERTISERS_CREATIVES_EDIT_URL, {
      advertiserId: creative.advertiserId.toString(),
      creativeId: creative.id.toString(),
      '*': `?${queryString.stringify({
        adGroupId: adGroupId || creative.association?.campaignId || '',
        targetedCreativeId: getTargetedCreativeId(creative),
        productId: productId || '',
        contractId,
      })}`,
    })}`;

    if (newTab) {
      window.open(editLink, '_blank');
      return;
    }
    navigate(editLink);
  };

  const associate = async (isAssociate, creative) => {
    const creativeId = creative.id;
    const selectedCreative = creativesList.collection?.find((item) => item.id === creativeId);
    const targetedCreativeId = get(selectedCreative, 'association.targetedCreativeId');

    try {
      if (isAssociate) {
        const payload = snakeCaseKeys({
          creativeId,
          productId,
          targetedCreativeId,
        });
        await postRequestJSON(
          apiHost,
          generatePath(API_ADGROUP_CREATIVE_ATTACH_URL, {
            adGroupId: adGroupId?.toString() || '',
          }),
          payload
        );
      } else {
        await deleteRequestJSON(
          apiHost,
          generatePath(API_ADGROUP_CREATIVE_DETACH_URL, {
            targetedCreativeId: targetedCreativeId?.toString() || '',
            '*': `?${queryString.stringify({
              productId: productId || undefined,
            })}`,
          })
        );
      }
    } catch (e) {
      const errors = get(JSON.parse(e.message), 'errors', []);
      showSnackBar(errors.join(' '), 'error', {
        event: 'creative_associate_error',
        creative_id: creativeId,
        ad_group_id: adGroupId,
      });
      return;
    }

    // Treat the call resolving as the success signal rather than a truthy
    // response body. A detach can come back 204/empty, and gating on the body
    // skipped both the confirmation and the refetch below — leaving the checkbox
    // rendering the stale association it was just detached from (ADOPS-3034).
    showSnackBar(`The creative was successfully ${isAssociate ? 'associated' : 'unassociated'}`, 'success', {
      event: isAssociate ? 'creative_associated' : 'creative_unassociated',
      creative_id: creativeId,
      ad_group_id: adGroupId,
    });
    // The refetch is what updates the row: the checkbox renders straight off the
    // query data, so re-reading it beats hand-patching the cached association.
    refetch();
  };

  const isAssociated = (creative) =>
    creative?.association?.associated && associationType === AssociationType.Product
      ? !isEmpty(creative?.association?.productIds)
      : creative?.association?.targetedCreativeId;

  const getSchedule = (creative) => {
    let returnValue;
    if (creative.customSchedule?.enabled) {
      const { startDate, endDate } = creative.customSchedule;

      if (startDate && endDate) {
        returnValue = `${mmddyy(formatServerDate(startDate))} to ${mmddyy(formatServerDate(endDate))}`;
      }
    }

    return returnValue;
  };

  const columnsRaw: GridColDef[] = [
    !!adGroupId && {
      field: 'association',
      headerName: 'Associated',
      renderCell: (params) =>
        !isAggregateCell(params) && (
          <Checkbox
            checked={!!isAssociated(params.row)}
            onChange={(e) => associate(e.target.checked, params.row)}
            onClick={(event) => event.stopPropagation()}
          />
        ),
      type: 'custom',
      display: 'flex',
      width: 80,
    },
    {
      field: 'name',
      headerName: 'Name',
      renderCell: (params) => !isAggregateCell(params) && `${params.value} (${params.row.id})`,
      width: 400,
    },
    {
      headerName: 'Action',
      field: 'actions',
      renderCell: (params) =>
        !isAggregateCell(params) && (
          <Stack onClick={(e) => navigateToCreativeForm(params.row.id, e.metaKey)} sx={{ cursor: 'pointer' }}>
            <Tooltip title="Edit Creative" placement="top-end">
              <FontAwesomeIcon icon={faPenToSquare} color="rgba(0, 0, 0, 0.54)" />
            </Tooltip>
          </Stack>
        ),
      flex: 1,
      type: 'actions',
    },
    {
      field: 'adType',
      headerName: 'Type',
      renderCell: (params) => !isAggregateCell(params) && getAdTypeLabel(params.value),
    },
    {
      field: 'size',
      headerName: 'Size',
      flex: 1,
    },
    {
      field: 'preview',
      headerName: 'Preview',
      renderCell: (params) => !isAggregateCell(params) && getPreview(params.row),
      display: 'flex',
      flex: 3,
      disableColumnMenu: true,
    },
    {
      field: 'previewStudio',
      headerName: 'Preview Studio',
      renderCell: (params) =>
        !isAggregateCell(params) && (
          <Button
            size="small"
            variant="text"
            component={RouterLink}
            to={`${PREVIEW_STUDIO_URL}?${queryString.stringify({
              campaign_id: adGroupId || params.row.association?.campaignId,
              creative_id: params.row.id,
            })}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Preview Studio
          </Button>
        ),
      display: 'flex',
      disableColumnMenu: true,
    },
    {
      field: 'qrPreview',
      headerName: 'QR Preview',
      renderCell: (params) =>
        !isAggregateCell(params) && (
          <QRCode
            opacity={0.2}
            previewJsx={getPreview(params.row)}
            size={80}
            title={params.row.name}
            url={`adops://ad_preview_qr?insertion_order_id=${
              lineItemId || params.row.association?.insertionOrderId
            }&campaign_id=${adGroupId || params.row.association?.campaignId}&creative_id=${params.row.id}`}
          />
        ),
      display: 'flex',
      disableColumnMenu: true,
    },
    {
      field: 'schedule',
      headerName: 'Schedule',
      renderCell: (params) => !isAggregateCell(params) && getSchedule(params.row),
      disableColumnMenu: true,
      // Fit "MM/DD/YY to MM/DD/YY" without truncation (ADOPS-2682 AC: date field 100% visible).
      width: 220,
    },
  ];

  const columns: GridColDef[] = columnsRaw.filter(Boolean);

  return (
    <DataGrid
      columns={columns}
      rows={creativesList?.collection || []}
      rowCount={creativesList?.pagination?.totalItems}
      loading={isLoading}
      disableColumnSorting
      rowHeight={100}
      groupingColDef={{
        display: 'flex',
      }}
      isRowSelectable={(params) => !!params.row.id} // Only allow selection if the row has an id (not a group row)
      disableAggregation
      {...rest}
      columnWidthStorageKey={COLUMN_WIDTHS_KEY}
    />
  );
}

export default CreativesDataGrid;
